<template>
  <div class="search-results">
    <section
      v-if="
        (searchResults.goals && searchResults.goals.length > 0) ||
          (searchResults.krs && searchResults.krs.length > 0)
      "
    >
      <h3 class="section-heading text-mini-1">Goals and KRs</h3>
      <div
        class="search-item d-flex selectable"
        v-for="goal in searchResults.goals"
        :key="'goal-' + goal.id"
        :data-location="JSON.stringify(getGoalLocation(goal))"
      >
        <i class="goal-icon icon-wrapper"></i>
        <router-link
          @click.native="$emit('item-click')"
          :to="getGoalLocation(goal)"
          class="text-normal text-primary text-decoration-none"
          >{{ goal.title }}
        </router-link>
      </div>
      <div
        class="search-item selectable"
        v-for="kr in searchResults.krs"
        :key="'kr-' + kr.id"
        :data-location="JSON.stringify(getKrLocation(kr))"
      >
        <div class="d-flex">
          <i class="kr-icon icon-wrapper"></i>
          <router-link
            @click.native="$emit('item-click')"
            :to="getKrLocation(kr)"
            class="text-normal text-primary text-decoration-none"
            >{{ kr.title }}
          </router-link>
        </div>
        <div class="d-flex align-items-center kr-goal">
          <i class="goal-icon-greyed mr-2 flex-shrink-0"></i>
          <span class="text-truncate text-small text-secondary">{{
            getOrFetchGoalTitle(kr.goalId, kr.organizationId)
          }}</span>
        </div>
      </div>
    </section>
    <section v-if="searchResults.projects && searchResults.projects.length > 0">
      <h3 class="section-heading text-mini-1">Projects</h3>
      <div
        class="search-item d-flex selectable align-items-baseline"
        v-for="project in searchResults.projects"
        :key="'project-' + project.id"
        :data-location="JSON.stringify(getProjectLocation(project))"
      >
        <project-icon :color="project.color" />
        <router-link
          @click.native="$emit('item-click')"
          :to="getProjectLocation(project)"
          class="text-normal text-primary text-decoration-none ml-2"
          >{{ project.title }}
        </router-link>
      </div>
    </section>
    <section v-if="searchResults.initiatives && searchResults.initiatives.length > 0">
      <h3 class="section-heading text-mini-1">
        {{ isTasksFeatureEnabled ? 'Initiatives and Tasks' : 'Initiatives' }}
      </h3>
      <div
        class="search-item d-flex selectable"
        v-for="initiative in openInitiatives"
        :key="'initiative-' + initiative.id"
        :data-location="JSON.stringify(getInitiativeLocation(initiative))"
      >
        <div class="icon-wrapper">
          <b-form-checkbox :checked="initiative.isDoneAndClosed" :disabled="true"></b-form-checkbox>
        </div>
        <div>
          <router-link
            @click.native="$emit('item-click')"
            :to="getInitiativeLocation(initiative)"
            class="text-normal text-primary text-decoration-none"
            >{{ initiative.title }}
          </router-link>
          <div
            class="text-small text-secondary"
            :class="{ invisible: !getOrFetchInitiativeParent(initiative) }"
          >
            <project-icon
              v-if="initiative.projectId"
              :color="getProjectColor(initiative.projectId)"
            />
            <i class="kr-icon-greyed mr-2" v-else></i>
            {{ getOrFetchInitiativeParent(initiative) }}
          </div>
        </div>
      </div>
      <b-button
        @click="closedInitiativesShown = !closedInitiativesShown"
        class="text-secondary text-medium show-completed-initiatives"
        variant="link"
        v-if="closedInitiatives.length > 0"
        >See completed initiatives <span v-if="isTasksFeatureEnabled">/ tasks</span>
      </b-button>
      <b-collapse v-model="closedInitiativesShown">
        <div
          class="search-item d-flex"
          :class="{ selectable: closedInitiativesShown }"
          v-for="initiative in closedInitiatives"
          :key="'initiative-' + initiative.id"
          :data-location="JSON.stringify(getInitiativeLocation(initiative))"
        >
          <div class="icon-wrapper">
            <b-form-checkbox
              :checked="initiative.isDoneAndClosed"
              :disabled="true"
            ></b-form-checkbox>
          </div>
          <div>
            <router-link
              @click.native="$emit('item-click')"
              :to="getInitiativeLocation(initiative)"
              class="text-normal text-primary text-decoration-none"
              >{{ initiative.title }}
            </router-link>
            <div
              class="text-small text-secondary"
              :class="{ invisible: !getOrFetchInitiativeParent(initiative) }"
            >
              <project-icon
                v-if="initiative.projectId"
                :color="getProjectColor(initiative.projectId)"
              />
              <i class="kr-icon-greyed mr-2" v-else></i>
              {{ getOrFetchInitiativeParent(initiative) }}
            </div>
          </div>
        </div>
      </b-collapse>
    </section>
    <section v-if="searchResults.comments && searchResults.comments.length > 0">
      <h3 class="section-heading text-mini-1">Comments</h3>
      <div
        class="search-item d-flex selectable"
        v-for="comment in searchResults.comments"
        :key="'comment' + comment.id"
        :data-location="JSON.stringify(getCommentPageLocation(comment))"
      >
        <i class="icon-wrapper quote-icon"></i>
        <div>
          <router-link
            @click.native="$emit('item-click')"
            :to="getCommentPageLocation(comment)"
            class="text-normal text-primary text-decoration-none comment"
            >{{ convertHTMLToPlainText(comment.comment) }}
          </router-link>
          <div class="text-small text-secondary d-flex align-items-center">
            <i class="kr-icon-greyed mr-2" v-if="comment.keyResultId"></i>
            <i class="goal-icon-greyed mr-2" v-else-if="comment.otype === 'goal_event'"></i>
            <b-form-checkbox
              class="mini-checkbox"
              :checked="isInitiativeDoneAndClosed(comment)"
              :disabled="true"
              v-else-if="comment.otype === 'initiative_event'"
            ></b-form-checkbox>
            <span>{{ getFetchCommentEntityTitle(comment) }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ProjectIcon from '@/components/project/ProjectIcon.vue'
import { featureFlags } from '@/constants/featureFlags'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import {
  getGoalPageRouteConfig,
  getInitiativePageRouteConfig,
  getKrPageRouteConfig,
  getProjectPageRouteConfig
} from '@/utils/navigationConfigs'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { BFormCheckbox, BCollapse, BButton } from 'bootstrap-vue'
import convertHTMLToPlainText from '@/utils/convertHTMLToPlainText'

export default {
  name: 'SpotlightSearchResults',
  props: ['searchResults', 'subOrgId', 'orgId'],
  components: {
    ProjectIcon,
    BFormCheckbox,
    BCollapse,
    BButton
  },
  mixins: [FeaturesMixin],
  data() {
    return {
      krs: {},
      closedInitiativesShown: false,
      isTasksFeatureEnabled: this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_TASKS)
    }
  },
  computed: {
    openInitiatives() {
      const initiatives = R.propOr([], 'initiatives', this.searchResults)
      return initiatives.filter(x => !x.isClosed)
    },
    closedInitiatives() {
      const initiatives = R.propOr([], 'initiatives', this.searchResults)
      return initiatives.filter(x => x.isClosed)
    }
  },
  methods: {
    ...mapActions(['getKRAlone', 'getGoal', 'getInitiative']),
    ...mapActions('project', ['getProject']),
    getProjectColor(projectId) {
      return this.$store.getters['project/projectById'](projectId)?.color
    },
    getOrFetchProjectTitle(initiative) {
      const projectId = initiative.projectId
      const project = this.$store.getters['project/projectById'](projectId)
      if (project) {
        return project.title
      } else {
        this.getProject({
          projectId: projectId,
          orgId: initiative.organizationId
        })
      }
    },
    getOrFetchKrTitle(entity) {
      const krId = entity.keyResultId
      const kr = this.$store.getters.keyResultById(krId) || this.krs[krId]
      if (kr) {
        return kr.title
      } else {
        this.getKRAlone({
          krId: krId,
          goalId: entity.goalId,
          orgId: entity.organizationId
        }).then(kr => {
          this.krs = { ...this.krs, [krId]: kr }
        })
      }
    },
    getOrFetchGoalTitle(goalId, orgId) {
      const goal = this.$store.getters.goalById(goalId)
      if (goal) {
        return goal.title
      } else {
        this.getGoal({
          goalId,
          orgId
        })
      }
    },
    getOrFetchInitiativeParent(initiative) {
      if (initiative.projectId) return this.getOrFetchProjectTitle(initiative)
      else if (initiative.keyResultId) return this.getOrFetchKrTitle(initiative)
    },
    getOrFetchInitiative(entity) {
      const initiativeId = entity.initiativeId
      const initiative = this.$store.getters.initiativeById(initiativeId)
      if (initiative) {
        return initiative
      } else {
        this.getInitiative({
          initiativeId,
          orgId: entity.organizationId
        })
      }
    },
    getFetchCommentEntityTitle(comment) {
      if (comment.otype === 'initiative_event') {
        const initiative = this.getOrFetchInitiative(comment)
        return initiative && initiative.title
      } else if (comment.type === 'checkin') {
        return this.getOrFetchKrTitle(comment)
      } else if (comment.otype === 'goal_event') {
        if (comment.keyResultId) {
          return this.getOrFetchKrTitle(comment)
        } else {
          return this.getOrFetchGoalTitle(comment.goalId, comment.organizationId)
        }
      }
    },
    isInitiativeDoneAndClosed(entity) {
      const initiative = this.getOrFetchInitiative(entity)
      return initiative && initiative.isDoneAndClosed
    },
    convertHTMLToPlainText,
    getCommentPageLocation(comment) {
      let location = {}
      if (comment.otype === 'initiative_event') {
        location = getInitiativePageRouteConfig({
          orgId: comment.organizationId,
          initiativeId: comment.initiativeId,
          query: {
            event: comment.id
          }
        })
      } else if (comment.type === 'checkin') {
        location = getKrPageRouteConfig({
          orgId: comment.organizationId,
          krId: comment.keyResultId,
          query: {
            event: comment.id
          }
        })
      } else if (comment.otype === 'goal_event') {
        if (comment.keyResultId) {
          location = getKrPageRouteConfig({
            orgId: comment.organizationId,
            krId: comment.keyResultId,
            query: {
              event: comment.id
            }
          })
        } else {
          location = getGoalPageRouteConfig({
            orgId: comment.organizationId,
            goalId: comment.goalId,
            query: {
              event: comment.id
            }
          })
        }
      }
      return location
    },
    getGoalLocation(goal) {
      return getGoalPageRouteConfig({
        orgId: goal.organizationId,
        goalId: goal.id
      })
    },
    getProjectLocation(project) {
      return getProjectPageRouteConfig({
        orgId: project.organizationId,
        projectId: project.id
      })
    },
    getKrLocation(kr) {
      return getGoalPageRouteConfig({
        orgId: kr.organizationId,
        goalId: kr.goalId,
        query: {
          krId: kr.id
        }
      })
    },
    getInitiativeLocation(initiative) {
      return getInitiativePageRouteConfig({
        orgId: initiative.organizationId,
        initiativeId: initiative.id
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

$side-spacing: 40px;

.section-heading {
  padding: 0 $side-spacing;
  text-transform: uppercase;
  color: $prime;
  margin-bottom: 6px;
}

.search-item {
  padding: 10px $side-spacing;
}

.goal-icon {
}

.icon-wrapper {
  width: 24px;
  height: 24px;
  background-position: center;
  background-size: auto;
  margin-right: 7px;
  flex-shrink: 0;
}

.title-label {
  color: $secondary;
  letter-spacing: 1px;
  margin-right: 6px;
}

.icon-wrapper /deep/ .custom-checkbox {
  padding-left: 28px;
}

/deep/ .mini-checkbox {
  min-height: 1.2rem;
  padding-left: 1.1rem;

  label:after {
    top: 0.2rem;
    left: -1.1rem;
    width: 0.6rem;
    height: 0.6rem;
  }

  input:checked + label:before {
    background-color: $tertiary !important;
    border-color: $secondary;
  }

  label:before {
    border-radius: 0.15rem;
    top: 0.25rem;
    left: -1.1rem;
    width: 0.6rem;
    height: 0.6rem;
  }
}

.selectable.selected {
  background-color: $selectionColor;
}

.search-results > section:not(:last-child) {
  margin-bottom: 15px;
}

.quote-icon {
  background-position: 7px 6px;
}

.comment {
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.invisible {
  opacity: 0;
}

.goal-icon-greyed {
  height: 10px;
  width: 10px;
  background-size: contain;
}

.kr-icon-greyed {
  height: 10px;
  width: 10px;
  background-size: contain;
}

.show-completed-initiatives {
  margin-left: 57px;
}

.kr-goal {
  padding-left: 32px;
}
</style>
